import { BaseApiService, API_CONFIG } from './base.service';
import {
  Company,
  CompanyCreate,
  CompanyUpdate,
  Contract,
  CompanyStripeSettings,
  CompanyStripeSettingsUpdate,
  CompanyStripeSettingsValidationResponse,
} from '../../types';

/**
 * Service for managing companies
 */
export class CompanyService extends BaseApiService {
  async getCompanies(skip: number = 0, limit: number = 100, includeInactive: boolean = false): Promise<Company[]> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      include_inactive: includeInactive.toString()
    });
    return this.get(`${API_CONFIG.ENDPOINTS.COMPANIES}/?${params.toString()}`);
  }

  async getCompany(id: number): Promise<Company> {
    return this.get(`${API_CONFIG.ENDPOINTS.COMPANIES}/${id}`);
  }

  async createCompany(company: CompanyCreate): Promise<Company> {
    return this.post(`${API_CONFIG.ENDPOINTS.COMPANIES}/`, company);
  }

  async updateCompany(id: number, company: CompanyUpdate): Promise<Company> {
    return this.put(`${API_CONFIG.ENDPOINTS.COMPANIES}/${id}`, company);
  }

  async deleteCompany(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.COMPANIES}/${id}`);
  }

  async getCompanyContracts(companyId: number, skip: number = 0, limit: number = 100): Promise<Contract[]> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString()
    });
    return this.get(`${API_CONFIG.ENDPOINTS.COMPANIES}/${companyId}/contracts?${params.toString()}`);
  }

  async uploadCompanyLogo(id: number, formData: FormData): Promise<{ logo_url: string }> {
    const response = await this.api.post(`${API_CONFIG.ENDPOINTS.COMPANIES}/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async getCompanyLanguageByInvoice(invoiceId: string): Promise<{
    invoice_id: string;
    contract_id: string;
    company_id?: number;
    company_name?: string;
    company_language?: string;
    contract_language: string;
    source: 'company' | 'contract';
  }> {
    return this.get(`${API_CONFIG.ENDPOINTS.COMPANIES}/language/by-invoice/${invoiceId}`);
  }

  async getStripePaymentSettings(companyId: number): Promise<CompanyStripeSettings> {
    return this.get(`${API_CONFIG.ENDPOINTS.COMPANIES}/${companyId}/payment-settings/stripe`);
  }

  async updateStripePaymentSettings(
    companyId: number,
    payload: CompanyStripeSettingsUpdate
  ): Promise<CompanyStripeSettings> {
    return this.put(`${API_CONFIG.ENDPOINTS.COMPANIES}/${companyId}/payment-settings/stripe`, payload);
  }

  async validateStripePaymentSettings(
    companyId: number,
    payload?: CompanyStripeSettingsUpdate
  ): Promise<CompanyStripeSettingsValidationResponse> {
    return this.post(`${API_CONFIG.ENDPOINTS.COMPANIES}/${companyId}/payment-settings/stripe/validate`, payload || {});
  }
}

export const companyService = new CompanyService();
